import React from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'

import { IBankInstitution } from '@/types'

interface ConfirmDisplayOrderDialogProps {
  open: boolean
  institution: IBankInstitution
  displayOrder: number
  isSaving: boolean
  // Set after a save failed; the dialog stays open so it can be retried or backed out of.
  error?: string
  onCancel: () => void
  onConfirm: () => void
}

const describe = (value: number | undefined) => (typeof value === 'number' ? String(value) : 'N/A')

// The display order decides where a bank sits in the list customers pick from, so the change is
// shown back before it is sent rather than applied on a keystroke.
export const ConfirmDisplayOrderDialog: React.FC<ConfirmDisplayOrderDialogProps> = ({
  open,
  institution,
  displayOrder,
  isSaving,
  error,
  onCancel,
  onConfirm,
}) => (
  <Dialog
    open={open}
    // Closing mid-save would hide the outcome, so the dialog stays until the request settles.
    onClose={isSaving ? undefined : onCancel}
    maxWidth="xs"
    fullWidth
    aria-labelledby="confirm-display-order-title"
    sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
  >
    <DialogTitle id="confirm-display-order-title" sx={{ fontSize: '18px', fontWeight: 600 }}>
      Update display order
    </DialogTitle>

    <DialogContent>
      <Typography sx={{ fontSize: '14px' }}>
        Change the display order of <strong>{institution.name}</strong> from{' '}
        <strong>{describe(institution.displayOrder)}</strong> to <strong>{displayOrder}</strong>?
      </Typography>
      {displayOrder === 0 && (
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 1 }}>
          A display order of 0 lists the institution after every ranked one.
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          The update failed: {error}
        </Alert>
      )}
    </DialogContent>

    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} disabled={isSaving}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={isSaving}
        startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
      >
        {error ? 'Try again' : 'Confirm'}
      </Button>
    </DialogActions>
  </Dialog>
)
